/**
 * Admin-authored announcement bars, stacked at the very top of the app shell
 * (mounted beside `ImpersonationBanner` in `app-shell.tsx` so they span the
 * full viewport width above the sidebar + content row).
 *
 * Content comes from `GET /api/banners` — already filtered server-side to the
 * viewer's personas/organizations, the banner's time window, and undismissed.
 * Renders nothing while loading: banners are progressive enhancement and a
 * skeleton bar would jank the shell on every cold load.
 */

import { alertVariants } from '@ordo/ui/alert'
import { ChevronDown, X } from 'lucide-react'
import { useState } from 'react'

import { type BannerDto, bannerDtoLinks, useActiveBanners, useDismissBanner } from '@/api/banners'
import { bannerIcon } from '@/lib/banner-icons'
import { isHandoffLink } from '@/lib/banner-links'
import { cn } from '@/lib/utils'

// Mirrors BANNER_COLORS in @ordo/schema (the alert variants); unknown values
// from future schema versions fall back to info.
type BannerVariant = 'info' | 'warning' | 'success' | 'error'
const KNOWN_COLORS = new Set<string>(['info', 'warning', 'success', 'error'])

function bannerVariant(color: string): BannerVariant {
  return (KNOWN_COLORS.has(color) ? color : 'info') as BannerVariant
}

export function AnnouncementBanners() {
  const { data } = useActiveBanners()
  const dismiss = useDismissBanner()

  const banners = data?.banners ?? []
  if (banners.length === 0) {
    return null
  }

  return (
    <div data-testid="announcement-banners">
      {banners.map((banner) => (
        <BannerBar key={banner.id} banner={banner} onDismiss={() => dismiss.mutate(banner.id)} />
      ))}
    </div>
  )
}

/** Body visibility classes. Below `sm` the body doesn't fit on the bar, so it
 * starts hidden and the chevron toggles it open as a block under the title;
 * from `sm` up it is always inline regardless of the toggle. Exported for
 * tests — SSR can't flip component state. */
export function bannerBodyClass(expanded: boolean): string {
  return expanded ? 'mt-1 block text-sm sm:ml-2 sm:mt-0 sm:inline' : 'ml-2 hidden text-sm sm:inline'
}

/** One announcement bar. Presentational — dismissal wiring is the caller's
 * (server-side per-user rows in the app shell, localStorage pre-login). */
export function BannerBar({ banner, onDismiss }: { banner: BannerDto; onDismiss: () => void }) {
  const Icon = bannerIcon(banner.icon)
  const links = bannerDtoLinks(banner)
  const [expanded, setExpanded] = useState(false)
  return (
    <div
      data-testid={`announcement-banner-${banner.id}`}
      className={cn(
        alertVariants({ variant: bannerVariant(banner.color) }),
        'flex w-full items-center gap-3 rounded-none border-x-0 border-t-0 px-4 py-2',
      )}>
      <Icon className="h-4 w-4 shrink-0" aria-hidden />
      <div className="min-w-0 flex-1">
        <span className="text-sm font-medium">{banner.title}</span>
        {banner.body ? <span className={bannerBodyClass(expanded)}>{banner.body}</span> : null}
        {links.map((link, index) => (
          <span key={`${link.url}-${index}`}>
            {/* Multiple calls-to-action read as one run: "Call us | Email us". */}
            {index > 0 ? (
              <span className="ml-2 text-sm opacity-50" aria-hidden>
                |
              </span>
            ) : null}
            <a
              href={link.url}
              // tel:/mailto: hand off to the dialer or mail client — a new tab
              // would be left blank. Web links keep the new-tab behavior.
              {...(isHandoffLink(link.url) ? {} : { target: '_blank', rel: 'noopener noreferrer' })}
              className="ml-2 text-sm font-medium underline underline-offset-2">
              {link.text || 'Learn more'}
            </a>
          </span>
        ))}
      </div>
      {banner.body ? (
        <button
          type="button"
          aria-expanded={expanded}
          aria-label={expanded ? 'Show less' : 'Show more'}
          onClick={() => setExpanded((v) => !v)}
          className="shrink-0 rounded p-1 opacity-70 transition-opacity hover:opacity-100 sm:hidden">
          <ChevronDown className={cn('h-4 w-4 transition-transform', expanded && 'rotate-180')} aria-hidden />
        </button>
      ) : null}
      {banner.dismissible ? (
        <button
          type="button"
          aria-label="Dismiss"
          onClick={onDismiss}
          className="shrink-0 rounded p-1 opacity-70 transition-opacity hover:opacity-100">
          <X className="h-4 w-4" aria-hidden />
        </button>
      ) : null}
    </div>
  )
}
